import { useState, ChangeEvent } from 'react';

import { Commander } from '../bridge';
import { ALL_SYSTEMS, ALL_SHAPES } from '../state';
import { TKSystem } from '../types';

/**
 * == Third level hook for: `filter-symbol-section` component ==
 *
 * @date 2024/09/27
 * @param currentFilter global symbol filter
 * @param onSharedFilterChange symbol filter callback to notify shared filter state in `useSharedFilter`
 */
export const useSymbolFilters = (
  currentFilter: string,
  onSharedFilterChange: (filter: string) => void,
) => {
  const [inverseChecked, setInverseChecked] = useState(false);
  const [lockEmptySystemChecked, setLockEmptySystemChecked] = useState(false);
  const [sizeHiddenChecked, setSizeHiddenChecked] = useState(true);
  // cache selected system
  const [selectedSystem, setSelectedSystem] = useState(ALL_SYSTEMS.value);
  // cache selected shape
  const [selectedShape, setSelectedShape] = useState(ALL_SHAPES.value);

  const menuDropDownOpenHandler = (nameInClass: string = 'mlm') => {
    const selector = `ul.nested-menu-dropdown.${nameInClass}`;
    const menuItems = document.querySelector(selector) as HTMLElement;
    menuItems.classList.toggle('show');
    // dont change this time `100ms`, other cause dropdown stuck
    setTimeout(() => menuItems.classList.add('fade-in'), 100);
  };

  const closeDropdownMenuHandler = () => {
    const selector = 'ul.nested-menu-dropdown';
    const menuItems = document.querySelectorAll(selector);
    [...menuItems].forEach((elm) => {
      (elm as HTMLElement).classList.remove('show');
    });
  };

  /**
   * only display part of detections
   * all symbols: `FILTERS.ALL`
   * @param type
   * @returns
   */
  const handleFilterChange = (type: string) => {
    setInverseChecked(false);
    closeDropdownMenuHandler();
    // check filter if changed
    if (type === currentFilter) return; // no change, no filter

    // == save it to global shared filter for both ==
    onSharedFilterChange(type);

    // notify map that filter changed to filter out symbols!
    Commander.doFilterSymbols(type);
  };

  /**
   * Filter out symbols from drawing with selected system
   * @param system selected system from drop-down menu
   */
  const handlerSystemChange = (system: string) => {
    closeDropdownMenuHandler();
    setSelectedSystem(system);
    Commander.doFilterBySystem(system);
  };

  const handleShapeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const shape = event.target.value;
    setSelectedShape(shape);
    Commander.doFilterByShape(shape);
  };

  // if true, change all the boxes white
  const reverseCheckHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setInverseChecked(checked);
    Commander.doReverseSymbols(checked);
  };

  /**
   * same effect as `reverseCheckHandler`, but callable from a keyboard
   * shortcut where there's no real checkbox `ChangeEvent` to read from.
   * @2026/09/15
   */
  const reverseCheckToggle = () => {
    const checked = !inverseChecked;
    setInverseChecked(checked);
    Commander.doReverseSymbols(checked);
  };

  // if true, lock symbols that have no `system` assigned
  const lockEmptySystemHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setLockEmptySystemChecked(checked);
    Commander.doLockEmptySystem(checked);
  };

  /**
   * same effect as `lockEmptySystemHandler`, but callable from a keyboard
   * shortcut where there's no real checkbox `ChangeEvent` to read from.
   * @2026/09/15
   */
  const lockEmptySystemToggle = () => {
    const checked = !lockEmptySystemChecked;
    setLockEmptySystemChecked(checked);
    Commander.doLockEmptySystem(checked);
  };

  /** @deprecated in favor of size filter - @2025/08/21 */
  const toggleSizeDisplay = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSizeHiddenChecked(checked);
    Commander.doToggleSizeText(checked);
  };

  const systemByShort = (short: string, systems: TKSystem[]) => {
    const systemFull = systems.find((s) => s.short == short)?.fullName;
    if (!systemFull) return short;
    const limit = 10;
    if (systemFull.length > limit) {
      return `${systemFull.substring(0, limit)}...`;
    }
    return systemFull;
  };

  return {
    sizeHiddenChecked,
    inverseChecked,
    lockEmptySystemChecked,
    selectedSystem,
    selectedShape,
    systemByShort,
    handleFilterChange,
    handlerSystemChange,
    handleShapeChange,
    reverseCheckHandler,
    reverseCheckToggle,
    lockEmptySystemHandler,
    lockEmptySystemToggle,
    toggleSizeDisplay,
    menuDropDownOpenHandler,
    closeDropdownMenuHandler,
  };
};
